'use client';

/**
 * Live permission provider (GitLab #808).
 *
 * `usePermission` used to answer each gate with its own request, resolved
 * once and then held for the life of the session — so a role change, and
 * worse a revocation, never reached an open session. A user had to sign out
 * and back in to see the menu they had just been granted.
 *
 * This provider fixes both halves of that. Every gate registers its slug,
 * registrations from one render pass are collected into a single
 * `GET /api/auth/permissions` request, and the whole registered set is
 * revalidated when the organisation changes, when the window regains focus
 * or becomes visible, and on a short interval. `refreshOrganizations()` runs
 * with it so organisation feature toggles (the reporting master switch) land
 * in the same pass rather than needing their own re-login.
 *
 * Previous answers are held across a revalidation so the sidebar does not
 * flicker; they are dropped outright on an organisation switch, where they
 * mean nothing.
 */

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '@/lib/auth/AuthContext';

export interface PermissionContextValue {
  /**
   * Organisation the map belongs to. A gate asking about a different
   * organisation must not read this map — `usePermission` falls back to its
   * own probe in that case.
   */
  orgId: string | null;
  /** Resolved answers for `orgId`. Absent slug means "not yet answered". */
  permissions: Record<string, boolean>;
  /** Declare interest in a slug. Batched into the next request. */
  register: (slug: string) => void;
  /** Revalidate every registered slug, plus the organisation list. */
  refresh: () => void;
  error: string | null;
}

export const PermissionContext = createContext<PermissionContextValue | null>(
  null,
);

/** Interval revalidation. Short enough to read as immediate to a user. */
export const REVALIDATE_INTERVAL_MS = 30_000;
/** Window in which registrations from one render pass are collected. */
export const BATCH_WINDOW_MS = 25;

interface PermissionsResponse {
  success: boolean;
  permissions?: Record<string, boolean>;
  error?: string;
}

/** Stable empty map, so the context value does not change identity needlessly. */
const EMPTY_PERMISSIONS: Record<string, boolean> = {};

/** Resolved answers, stamped with the organisation they belong to. */
interface ResolvedPermissions {
  orgId: string | null;
  map: Record<string, boolean>;
  error: string | null;
}

function applyError(
  prev: ResolvedPermissions,
  forOrgId: string,
  error: string,
): ResolvedPermissions {
  return prev.orgId === forOrgId
    ? { ...prev, error }
    : { orgId: forOrgId, map: {}, error };
}

export function PermissionProvider({
  children,
  revalidateIntervalMs = REVALIDATE_INTERVAL_MS,
}: {
  children: React.ReactNode;
  revalidateIntervalMs?: number;
}) {
  const { currentOrganization, refreshOrganizations } = useAuth();
  const orgId = currentOrganization?.id ?? null;

  // Answers are stamped with the organisation they were resolved for, so a
  // response that lands after a switch cannot be read as the new
  // organisation's, and a switch needs no state reset to invalidate them.
  const [resolved, setResolved] = useState<ResolvedPermissions>(() => ({
    orgId,
    map: {},
    error: null,
  }));

  const registeredRef = useRef<Set<string>>(new Set());
  const batchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const orgIdRef = useRef<string | null>(orgId);
  // `refreshOrganizations` is rebuilt on every AuthProvider render; holding it
  // in a ref keeps it out of the effect dependency lists below.
  const refreshOrganizationsRef = useRef(refreshOrganizations);

  // Declared first so the refs are current before the effects below read them.
  useEffect(() => {
    orgIdRef.current = orgId;
    refreshOrganizationsRef.current = refreshOrganizations;
  }, [orgId, refreshOrganizations]);

  // A batch window still open at unmount would otherwise fire a request for a
  // provider that no longer exists.
  useEffect(
    () => () => {
      if (batchTimerRef.current) clearTimeout(batchTimerRef.current);
      batchTimerRef.current = null;
    },
    [],
  );

  const fetchSlugs = useCallback(
    async (slugs: string[], forOrgId: string) => {
      if (slugs.length === 0) return;
      // This request already covers every registered slug, so a batch window
      // still open would only repeat it a few milliseconds later.
      if (batchTimerRef.current) {
        clearTimeout(batchTimerRef.current);
        batchTimerRef.current = null;
      }
      const params = new URLSearchParams({
        slugs: slugs.join(','),
        organization_id: forOrgId,
      });
      try {
        const res = await fetch(`/api/auth/permissions?${params.toString()}`);
        const body = (await res.json()) as PermissionsResponse;
        if (orgIdRef.current !== forOrgId) return;
        if (!res.ok || !body.success || !body.permissions) {
          const message =
            body.error ?? `Request failed with status ${res.status}`;
          setResolved((prev) => applyError(prev, forOrgId, message));
          return;
        }
        const answers = body.permissions;
        setResolved((prev) => ({
          orgId: forOrgId,
          map: prev.orgId === forOrgId ? { ...prev.map, ...answers } : answers,
          error: null,
        }));
      } catch (err) {
        if (orgIdRef.current !== forOrgId) return;
        const message =
          err instanceof Error ? err.message : 'Permission check failed';
        setResolved((prev) => applyError(prev, forOrgId, message));
      }
    },
    [],
  );

  const register = useCallback(
    (slug: string) => {
      if (registeredRef.current.has(slug)) return;
      registeredRef.current.add(slug);
      if (batchTimerRef.current) return;
      batchTimerRef.current = setTimeout(() => {
        batchTimerRef.current = null;
        if (!orgId) return;
        void fetchSlugs(Array.from(registeredRef.current), orgId);
      }, BATCH_WINDOW_MS);
    },
    [orgId, fetchSlugs],
  );

  const refresh = useCallback(() => {
    void refreshOrganizationsRef.current?.();
    if (!orgId) return;
    void fetchSlugs(Array.from(registeredRef.current), orgId);
  }, [orgId, fetchSlugs]);

  // Organisation switch: previous answers belong to the previous org, so they
  // stop being read the moment `orgId` changes and are re-resolved here.
  useEffect(() => {
    if (!orgId) return;
    void fetchSlugs(Array.from(registeredRef.current), orgId);
  }, [orgId, fetchSlugs]);

  // Focus, visibility and interval revalidation.
  useEffect(() => {
    if (!orgId) return;
    const onFocus = () => refresh();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const timer = setInterval(refresh, revalidateIntervalMs);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(timer);
    };
  }, [orgId, refresh, revalidateIntervalMs]);

  // Answers resolved for another organisation are not exposed at all, so a
  // gate reverts to loading on a switch rather than showing a stale answer.
  const isCurrent = resolved.orgId === orgId;
  const permissions = isCurrent ? resolved.map : EMPTY_PERMISSIONS;
  const error = isCurrent ? resolved.error : null;

  const value = useMemo<PermissionContextValue>(
    () => ({ orgId, permissions, register, refresh, error }),
    [orgId, permissions, register, refresh, error],
  );

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}
